import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ProcedureTableRow } from "../components/Procedures/ProcedureTableRow";
import { ProcedureActions } from "../components/Procedures/ProcedureActions";
import { Plus, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Search, Archive, Trash2, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { userHasAccessToDepartment, canUserViewProcedure, isUserViewerOfProcedure } from "../utils/procedure-permissions";
import { User } from "../types";
import { Pagination, PaginationContent, PaginationItem } from "../components/ui/pagination";
import { toSydneyDate } from "../utils/helpers";
import { Procedure } from '../types';
import { Can } from "../components/Can";
import { useAbility } from '@casl/react';
import { AbilityContext } from '../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";


export default function ProceduresPage() {
  const { departments, users, procedures, addProcedure, updateProcedure, deleteProcedure, updateActivity, archiveProcedure, unarchiveProcedure, trainingPlans } = useData();
  const { currentUser } = useAuth();

  const ability = useAbility(AbilityContext);

  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("active");

  const [searchTerm, setSearchTerm] = useState("");
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [procedureToArchive, setProcedureToArchive] = useState<any>(null);
  const [openUnarchiveDialog, setOpenUnarchiveDialog] = useState(false);
  const [procedureToUnarchive, setProcedureToUnarchive] = useState<any>(null);
  const [filteredProcedures, setFilteredProcedures] = useState<Procedure[]>([]);
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [procedureOwners, setProcedureOwners] = useState<(User)[]>([]);
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const uniqueOwnerMap = new Map<string, User>();

    procedures.forEach(procedure => {
      const owner = procedure.procedureOwner;
      if (owner && typeof owner === 'object' && owner.id) {
        if (!uniqueOwnerMap.has(owner.id)) {
          uniqueOwnerMap.set(owner.id, owner);
        }
      }
    });

    const uniqueOwners = Array.from(uniqueOwnerMap.values());

    setProcedureOwners(uniqueOwners);
  }, [procedures]);

  const itemsPerPage = 25;

  // Update handlers to reset page
  const handleDepartmentFilter = (value: string) => {
    setDepartmentFilter(value);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleShowArchived = (value: string) => {
    setActiveTab(value);
    setCurrentPage(1);
  };

  const handleOwnerFilter = (value: string) => {
    setOwnerFilter(value);
    setCurrentPage(1);
  };

  // Get all departments user has access to
  const accessibleDepartments = departments.filter(
    dept => currentUser && userHasAccessToDepartment(currentUser, dept.departmentCode, ability)
  );

  useEffect(() => {
    const filteredProcedures = procedures.filter(procedure => {
      // First check if user has access to this procedure
      const hasAccess = canUserViewProcedure(currentUser, procedure, ability);

      if (!hasAccess) return false;

      // Filter out deleted procedures
      if (procedure.deleted) return false;

      // Filter by tab
      if (activeTab === "archived") {
        if (!procedure.archived) return false;
      } else {
        if (procedure.archived) return false;

        const isViewer = isUserViewerOfProcedure(currentUser, procedure);
        if (activeTab === "view_member") {
          if (!isViewer) return false;
        } else if (activeTab === "active") {
          // If in 'active' tab, we show procedures where user is NOT just a viewer 
          // (i.e., they are owner, stakeholder, or in the department)
          if (isViewer) return false;
        }
      }
      if (ownerFilter !== "all") {
        const ownerId = typeof procedure.procedureOwner === 'object' && procedure.procedureOwner !== null 
          ? procedure.procedureOwner.id 
          : procedure.procedureOwner;
        return ownerId == ownerFilter;
      }

      // Then apply the department filter if not "all"
      if (departmentFilter !== "all") {
        // Find department by _id to get its departmentCode
        const selectedDept = departments.find(d => d.id === departmentFilter);
        return procedure.departmentId === selectedDept?.departmentCode;
      }

      // Add search filter
      if (searchTerm && !procedure.procedureName.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      return true;
    });

    setFilteredProcedures(filteredProcedures);
  }, [procedures, searchTerm, departmentFilter, activeTab, ownerFilter]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredProcedures.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProcedures = filteredProcedures.slice(startIndex, endIndex);

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  };

  const getUserName = (userIdOrObject: string | User | null | undefined): string => {
    if (!userIdOrObject) return "Not assigned";
    if (typeof userIdOrObject === 'string') {
      const user = users.find(u => u.id === userIdOrObject);
      return user ? user.name : "Unknown User";
    }
    return userIdOrObject.name || "Unknown User";
  };

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold">Procedures</h1>
        <div className="w-full md:w-auto">
          {activeTab === 'active' && (
            <Can I={ACTIONS.CREATE} a={RESOURCES.PROCEDURE}>
              <Link to="/procedure/new">
                <Button className="w-full flex items-center justify-center gap-2">
                  <Plus size={16} />
                  <span>Add Procedure</span>
                </Button>
              </Link>
            </Can>
          )}
        </div>
      </div>

      {/* Tabs for Active/Archived */}
      <Tabs defaultValue="active" className="w-full" onValueChange={handleShowArchived}>
        <div className="w-full overflow-x-auto">
          <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
            <TabsTrigger
              value="active"
              className="rounded-full px-4 py-2 data-[state=active]:bg-white"
            >
              Active
            </TabsTrigger>
            <TabsTrigger
              value="view_member"
              className="rounded-full px-4 py-2 data-[state=active]:bg-white"
            >
              View Member
            </TabsTrigger>
            <TabsTrigger
              value="archived"
              className="rounded-full px-4 py-2 data-[state=active]:bg-white"
            >
              Archived
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* Filters */}
      <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search procedures..."
          value={searchTerm}
          onChange={handleSearch}
          className="pl-10"
        />
        <div className="flex items-center w-full md:w-auto">
          <Select value={ownerFilter} onValueChange={handleOwnerFilter}>
            <SelectTrigger className="w-full md:w-[250px] truncate">
              <SelectValue placeholder="Filter by Owner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Owners</SelectItem>
              {procedureOwners.map(user => (
                <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center w-full md:w-auto">
          <Select value={departmentFilter} onValueChange={handleDepartmentFilter}>
            <SelectTrigger className="w-full md:w-[250px]">
              <SelectValue placeholder="Filter by Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {accessibleDepartments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.departmentCode} - {dept.departmentName} {dept.archived && "(Archived)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {showArchived ? 'Archived Procedures' : 'Active Procedures'}
          </CardTitle>
        </CardHeader>
        <CardContent> */}
      {/* Mobile View: List of Cards */}
      <div className="md:hidden space-y-4">
        {currentProcedures.length > 0 ? (
          currentProcedures.map((procedure) => (
            <Card key={procedure.id} className="p-4">
              <div className="flex flex-col gap-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-grow min-w-0">
                    <Link to={`/procedure/${procedure.id}`} className="font-semibold text-primary hover:underline block truncate">
                      {procedure.procedureName}
                    </Link>
                    <p className="text-sm text-muted-foreground truncate">
                      {procedure.department?.departmentCode || 'Unknown'} - {procedure.department?.departmentName || 'Unknown Department'}
                    </p>
                  </div>
                  <div className="flex items-center flex-shrink-0">
                    <ProcedureActions
                      procedure={procedure}
                      showArchived={activeTab === 'archived'}
                      departments={departments}
                      users={users}
                      trainingPlans={trainingPlans}
                      updateProcedure={updateProcedure}
                      archiveProcedure={archiveProcedure}
                      unarchiveProcedure={unarchiveProcedure}
                      currentUser={currentUser}
                      isViewerOnly={activeTab === 'view_member'}
                      variant="ghost"
                    />
                  </div>
                </div>
                <div className="text-sm space-y-1 text-muted-foreground">
                  <p><strong>Owner:</strong> {getUserName(procedure.procedureOwner)}</p>
                  <p><strong>Cadence:</strong> {procedure.procedureCadence}</p>
                  <p><strong>Created:</strong> {toSydneyDate(procedure.createdAt)}</p>
                  {activeTab === 'archived' && procedure.archivedOn && (
                    <p><strong>Archived:</strong> {toSydneyDate(procedure.archivedOn)}</p>
                  )}
                </div>
              </div>
            </Card>
          ))
        ) : (
          <p className="text-center text-muted-foreground py-8">No procedures found.</p>
        )}
      </div>

      {/* Desktop View: Table */}
      <Card className="hidden md:block overflow-x-auto">
        <CardContent className="p-3">
          {/* <div className="hidden md:block overflow-x-auto"> */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Cadence</TableHead>
                <TableHead>Created On</TableHead>
                {activeTab === 'archived' && <TableHead>Archived On</TableHead>}
                {!(activeTab === 'view_member') && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE}>
                    <TableHead className="text-right">Actions</TableHead>
                  </Can>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentProcedures.map((procedure) => (
                <ProcedureTableRow
                  key={procedure.id}
                  procedure={procedure}
                  canManageProcedures={true}
                  showArchived={activeTab === 'archived'}
                  isViewerOnly={activeTab === 'view_member'}
                  departments={departments}
                  users={users}
                  trainingPlans={trainingPlans}
                  updateProcedure={updateProcedure}
                  archiveProcedure={archiveProcedure}
                  unarchiveProcedure={unarchiveProcedure}
                  currentUser={currentUser}
                />
              ))}
              {currentProcedures.length === 0 && (
                <TableRow>
                  <TableCell colSpan={activeTab === 'archived' ? 7 : 6} className="text-center h-24">
                    No procedures found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {
        filteredProcedures.length > itemsPerPage && (
          <div className="mt-6 flex justify-center">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button variant="ghost" size="icon" onClick={() => handlePageChange(1)} disabled={currentPage === 1}><ChevronsLeft className="h-4 w-4" /></Button>
                </PaginationItem>
                <PaginationItem>
                  <Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                </PaginationItem>
                {getPageNumbers().map((pageNum) => (
                  <PaginationItem key={pageNum}>
                    <Button variant={currentPage === pageNum ? "outline" : "ghost"} onClick={() => handlePageChange(pageNum)} className="size-9">{pageNum}</Button>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <Button variant="ghost" size="icon" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button>
                </PaginationItem>
                <PaginationItem>
                  <Button variant="ghost" size="icon" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}><ChevronsRight className="h-4 w-4" /></Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )
      }
      {/* </CardContent>
      </Card> */}
    </div >
  );
}
